'use client'

import clsx from 'clsx'
import { useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Modal } from '@/Modal'

export const Gallery = ({
  title,
  images,
  /** If set, lightbox cycles through these (e.g. full set while showing fewer thumbnails). */
  lightboxImages,
  gridClassName = 'grid grid-cols-2 md:grid-cols-4 gap-3',
  thumbClassName = 'overflow-hidden rounded-md border-2 border-gray-300 hover:opacity-90 transition-opacity',
  imageClassName = 'w-full h-full object-cover',
  lightboxImageClassName,
}: GalleryProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  if (images.length === 0) return null

  const slides = lightboxImages?.length ? lightboxImages : images

  const openAt = (thumbnailIndex: number) => {
    setActiveIndex(thumbnailIndex)
    setIsOpen(true)
  }

  const goPrevious = () => {
    setActiveIndex(prev => (prev - 1 + slides.length) % slides.length)
  }

  const goNext = () => {
    setActiveIndex(prev => (prev + 1) % slides.length)
  }

  return (
    <>
      <div className={gridClassName}>
        {images.map((image, index) => (
          <button
            key={`${image.src}-${index}`}
            type="button"
            onClick={() => openAt(index)}
            className={thumbClassName}
            aria-label={`Open ${title} image ${index + 1}`}
          >
            <img
              src={image.src}
              alt={image.alt}
              className={imageClassName}
            />
          </button>
        ))}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="5xl"
        showCloseButton={true}
        className="m-4"
      >
        <div className="relative bg-black p-3 md:p-4">
          {slides.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2"
                aria-label="Previous image"
              >
                <ChevronLeftIcon className="w-5 h-5 text-black" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2"
                aria-label="Next image"
              >
                <ChevronRightIcon className="w-5 h-5 text-black" />
              </button>
            </>
          )}
          <img
            src={slides[activeIndex].src}
            alt={slides[activeIndex].alt}
            className={clsx(
              'w-full max-h-[80vh] object-contain',
              lightboxImageClassName
            )}
          />
        </div>
      </Modal>
    </>
  )
}

interface GalleryProps {
  title: string
  images: Array<{ src: string; alt: string }>
  lightboxImages?: Array<{ src: string; alt: string }>
  /** Tailwind grid classes for the thumbnail layout */
  gridClassName?: string
  /** Classes for each thumbnail button (border, hover) */
  thumbClassName?: string
  imageClassName?: string
  /** Classes for the full-size image in the lightbox (e.g. border accent) */
  lightboxImageClassName?: string
}
